import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { rescisaoApi, funcionariosApi } from '../../api/rh';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';

function formatarReais(v) {
  return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function formatarData(d) {
  return d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '-';
}

const OPCOES_MOTIVO = [
  { value: 'sem_justa_causa', label: 'Demissão Sem Justa Causa' },
  { value: 'pedido_demissao', label: 'Pedido de Demissão' },
  { value: 'justa_causa', label: 'Justa Causa' },
  { value: 'acordo_mutuo', label: 'Acordo Mútuo (Distrato)' },
  { value: 'termino_contrato', label: 'Término de Contrato' }
];
const MOTIVO_LABEL = Object.fromEntries(OPCOES_MOTIVO.map(o => [o.value, o.label]));

const OPCOES_AVISO = [
  { value: 'indenizado', label: 'Indenizado' },
  { value: 'trabalhado', label: 'Trabalhado' },
  { value: 'isento', label: 'Isento (pedido de demissão etc.)' }
];

const FORM_VAZIO = { funcionario_id: '', data_demissao: new Date().toISOString().slice(0, 10), motivo: 'sem_justa_causa', aviso_previo: 'indenizado', observacoes: '' };

export default function RescisaoList() {
  useTituloPagina('Rescisão');
  const [funcionarios, setFuncionarios] = useState([]);
  const [rescisoes, setRescisoes] = useState([]);
  const [form, setForm] = useState(FORM_VAZIO);
  const [calculo, setCalculo] = useState(null);
  const [calculando, setCalculando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = () => {
    funcionariosApi.listar({ status: 'ativo' }).then(setFuncionarios).catch(() => {});
    rescisaoApi.listar().then(setRescisoes).catch(() => {});
  };
  useEffect(() => { carregar(); }, []);

  const opcoesFuncionarios = [{ value: '', label: 'Selecione...' }, ...funcionarios.map(f => ({ value: f.id, label: f.nome }))];

  const calcular = async () => {
    if (!form.funcionario_id) { setErro('Selecione o funcionário'); return; }
    setCalculando(true);
    setErro('');
    setCalculo(null);
    try {
      const resultado = await rescisaoApi.calcular(form);
      setCalculo(resultado);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCalculando(false);
    }
  };

  const confirmar = async () => {
    if (!window.confirm('Confirmar a rescisão? O funcionário será marcado como demitido e não será possível desfazer por aqui.')) return;
    setSalvando(true);
    setErro('');
    try {
      await rescisaoApi.criar(form);
      setForm(FORM_VAZIO);
      setCalculo(null);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <p style={{ fontSize: 12, color: COLORS.textMid, marginTop: 0, background: COLORS.warningBg, padding: 10, borderRadius: 8 }}>
          <strong>Atenção:</strong> a multa de FGTS mostrada aqui é uma <strong>estimativa</strong> baseada no % de FGTS configurado × tempo de casa — o saldo real da conta vinculada só existe na Caixa. Valide com o contador antes de pagar.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
          <Select label="Funcionário" options={opcoesFuncionarios} value={form.funcionario_id} onChange={e => setForm(f => ({ ...f, funcionario_id: e.target.value }))} />
          <Input label="Data de Demissão" type="date" value={form.data_demissao} onChange={e => setForm(f => ({ ...f, data_demissao: e.target.value }))} />
          <Select label="Motivo" options={OPCOES_MOTIVO} value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} />
          <Select label="Aviso Prévio" options={OPCOES_AVISO} value={form.aviso_previo} onChange={e => setForm(f => ({ ...f, aviso_previo: e.target.value }))} />
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid, marginBottom: 4 }}>Observações</div>
          <textarea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} rows={2}
            style={{ width: '100%', border: `1.5px solid ${COLORS.border}`, borderRadius: 7, padding: '8px 9px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        {erro && <p style={{ color: COLORS.danger, fontSize: 13 }}>{erro}</p>}
        <div style={{ marginTop: 10 }}>
          <Btn variante="secondary" onClick={calcular} disabled={calculando}>{calculando ? 'Calculando...' : 'Calcular Rescisão'}</Btn>
        </div>

        {calculo && (
          <div style={{ marginTop: 16, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                <tr><td style={{ padding: 6, color: COLORS.textMid }}>Saldo de Salário</td><td style={{ padding: 6, textAlign: 'right' }}>{formatarReais(calculo.saldoSalarioValor)}</td></tr>
                <tr><td style={{ padding: 6, color: COLORS.textMid }}>Aviso Prévio ({calculo.diasAvisoPrevio} dias)</td><td style={{ padding: 6, textAlign: 'right' }}>{formatarReais(calculo.avisoPrevioValor)}</td></tr>
                <tr><td style={{ padding: 6, color: COLORS.textMid }}>Férias Vencidas</td><td style={{ padding: 6, textAlign: 'right' }}>{formatarReais(calculo.feriasVencidasValor)}</td></tr>
                <tr><td style={{ padding: 6, color: COLORS.textMid }}>Férias Proporcionais + 1/3</td><td style={{ padding: 6, textAlign: 'right' }}>{formatarReais(calculo.feriasProporcionaisValor)}</td></tr>
                <tr><td style={{ padding: 6, color: COLORS.textMid }}>13º Proporcional</td><td style={{ padding: 6, textAlign: 'right' }}>{formatarReais(calculo.decimoProporcionalValor)}</td></tr>
                <tr><td style={{ padding: 6, color: COLORS.textMid }}>Multa FGTS (estimada, base {formatarReais(calculo.fgtsBaseEstimada)})</td><td style={{ padding: 6, textAlign: 'right' }}>{formatarReais(calculo.multaFgtsValor)}</td></tr>
                <tr style={{ borderTop: `1px solid ${COLORS.border}` }}><td style={{ padding: 6, fontWeight: 700 }}>Total</td><td style={{ padding: 6, textAlign: 'right', fontWeight: 700, color: COLORS.primary }}>{formatarReais(calculo.totalValor)}</td></tr>
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
              <Btn variante="danger" onClick={confirmar} disabled={salvando}>{salvando ? 'Confirmando...' : 'Confirmar Rescisão e Demitir'}</Btn>
            </div>
          </div>
        )}
      </Card>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', background: COLORS.bg }}>
              <th style={{ padding: 10 }}>Funcionário</th>
              <th style={{ padding: 10 }}>Data</th>
              <th style={{ padding: 10 }}>Motivo</th>
              <th style={{ padding: 10 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rescisoes.map(r => (
              <tr key={r.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: 10, fontWeight: 600 }}>{r.funcionario_nome}</td>
                <td style={{ padding: 10 }}>{formatarData(r.data_demissao)}</td>
                <td style={{ padding: 10 }}><Badge tom="info">{MOTIVO_LABEL[r.motivo]}</Badge></td>
                <td style={{ padding: 10, fontWeight: 700 }}>{formatarReais(r.total_valor)}</td>
              </tr>
            ))}
            {rescisoes.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: COLORS.textMid }}>Nenhuma rescisão registrada ainda.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
